import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Card,
  CardContent,
  TextField,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  OutlinedInput,
  TablePagination,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Switch,
  FormControlLabel,
  Snackbar,
} from '@mui/material';
import { Edit, Delete, Add, Search, Close } from '@mui/icons-material';
import { apiService } from '@/services/api';
import { useTranslation } from '@/hooks/useTranslation';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  roleExternalId: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const EmployeesPage: React.FC = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [roles, setRoles] = useState<Array<{ externalId: string; name: string }>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [deletingEmployee, setDeletingEmployee] = useState<Employee | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const [newEmployee, setNewEmployee] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    roleId: '',
    password: '',
    isActive: true,
  });

  const normalizeEmployee = (employee: any): Employee => ({
    ...employee,
    roleId: employee.roleId ? String(employee.roleId) : '',
  });

  // Load employees from API
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiService.getEmployees(page + 1, rowsPerPage);
        setEmployees(response.employees.map(normalizeEmployee));
        setTotal(response.total);
      } catch (err: any) {
        setError(err.message || t('employees.errors.failedToLoad'));
        console.error('Error loading employees:', err);
      } finally {
        setLoading(false);
      }
    };

    loadEmployees();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await apiService.getRoles(1, 100);
        setRoles(response.roles.map(role => ({ externalId: role.externalId, name: role.name })));
      } catch (err) {
        console.error('Failed to load roles:', err);
      }
    };

    loadRoles();
  }, []);

  const handleEdit = (id: string) => {
    const employee = employees.find(e => e.id === id);
    if (employee) {
      setEditingEmployee(employee);
      setIsEditModalOpen(true);
    }
  };

  const handleDelete = (id: string) => {
    const employee = employees.find(e => e.id === id);
    if (employee) {
      setDeletingEmployee(employee);
      setIsDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingEmployee) return;

    try {
      await apiService.deleteEmployee(deletingEmployee.id);

      // Remove the employee from the list
      setEmployees(prev => prev.filter(e => e.id !== deletingEmployee.id));

      // Update total count
      setTotal(prev => prev - 1);

      // Show success message
      setSnackbar({
        open: true,
        message: t('employees.messages.deletedSuccess'),
        severity: 'success',
      });

      // Close dialog
      setIsDeleteDialogOpen(false);
      setDeletingEmployee(null);
    } catch (err: any) {
      console.error('Error deleting employee:', err);
      setSnackbar({
        open: true,
        message: err.message || t('employees.errors.failedToDelete'),
        severity: 'error',
      });
    }
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeletingEmployee(null);
  };

  const handleAddEmployee = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setNewEmployee({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      roleId: '',
      password: '',
      isActive: true,
    });
  };

  const handleSaveEmployee = async () => {
    // Validate required fields
    if (
      !newEmployee.firstName ||
      !newEmployee.lastName ||
      !newEmployee.email ||
      !newEmployee.password ||
      !newEmployee.phoneNumber ||
      !newEmployee.roleId
    ) {
      setSnackbar({
        open: true,
        message: t('employees.errors.fillRequiredFields'),
        severity: 'error',
      });
      return;
    }

    try {
      const employeeData = {
        email: newEmployee.email,
        password: newEmployee.password,
        firstName: newEmployee.firstName,
        lastName: newEmployee.lastName,
        phoneNumber: newEmployee.phoneNumber,
        roleExternalId: newEmployee.roleId,
        isActive: newEmployee.isActive,
      };

      const createdEmployee = await apiService.createEmployee(employeeData);

      // Add the new employee to the list
      setEmployees(prev => [...prev, normalizeEmployee(createdEmployee)]);

      // Update total count
      setTotal(prev => prev + 1);

      // Show success message
      setSnackbar({
        open: true,
        message: t('employees.messages.createdSuccess'),
        severity: 'success',
      });

      handleCloseModal();
    } catch (err: any) {
      console.error('Error creating employee:', err);
      setSnackbar({
        open: true,
        message: err.message || t('employees.errors.failedToCreate'),
        severity: 'error',
      });
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingEmployee(null);
  };

  const handleEditInputChange =
    (field: keyof Employee) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (editingEmployee) {
        setEditingEmployee(prev =>
          prev ? { ...prev, [field]: event.target.value } : null,
        );
      }
    };

  const handleEditSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (editingEmployee) {
      setEditingEmployee(prev =>
        prev ? { ...prev, isActive: event.target.checked } : null,
      );
    }
  };

  const handleUpdateEmployee = async () => {
    if (!editingEmployee) return;

    // Validate required fields
    if (
      !editingEmployee.firstName ||
      !editingEmployee.lastName ||
      !editingEmployee.email ||
      !editingEmployee.phoneNumber ||       !editingEmployee.roleExternalId
    ) {
      setSnackbar({
        open: true,
        message: t('employees.errors.fillRequiredFields'),
        severity: 'error',
      });
      return;
    }

    try {
      const employeeData = {
        firstName: editingEmployee.firstName,
        lastName: editingEmployee.lastName,
        email: editingEmployee.email,
        phoneNumber: editingEmployee.phoneNumber,
        roleExternalId: editingEmployee.roleExternalId,
        isActive: editingEmployee.isActive,
      };

      const updatedEmployee = await apiService.updateEmployee(
        editingEmployee.id,
        employeeData,
      );

      // Update the employee in the list
      setEmployees(prev =>
        prev.map(e =>
          e.id === editingEmployee.id ? normalizeEmployee(updatedEmployee) : e,
        ),
      );

      // Show success message
      setSnackbar({
        open: true,
        message: t('employees.messages.updatedSuccess'),
        severity: 'success',
      });

      handleCloseEditModal();
    } catch (err: any) {
      console.error('Error updating employee:', err);
      setSnackbar({
        open: true,
        message: err.message || t('employees.errors.failedToUpdate'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInputChange =
    (field: keyof typeof newEmployee) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewEmployee(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewEmployee(prev => ({
      ...prev,
      isActive: event.target.checked,
    }));
  };

  const handleRoleChange = (event: any) => {
    setNewEmployee(prev => ({
      ...prev,
      roleId: event.target.value as string,
    }));
  };

  // Filter employees based on search
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch =
      employee.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.phoneNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.createdAt.includes(searchTerm);

    return matchesSearch;
  });

  // Pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && employees.length === 0) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('employees.title')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          {/* Search and Add Employee Controls */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
            }}
          >
            <TextField
              placeholder={t('employees.searchPlaceholder')}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, maxWidth: { xs: '100%', sm: '400px' } }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddEmployee}
              sx={{ minWidth: '140px' }}
            >
              {t('employees.addEmployee')}
            </Button>
          </Box>

          {error && (
            <Alert variant="filled" severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Table */}
          <Box sx={{ width: '100%', overflowX: 'auto', minWidth: 0 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="h6">{t('employees.table.firstName')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('employees.table.lastName')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('employees.table.email')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('employees.table.phoneNumber')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('employees.table.role')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('employees.table.status')}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6">{t('employees.table.createdAt')}</Typography>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map(employee => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {employee.firstName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {employee.lastName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {employee.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {employee.phoneNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="primary">
                        {roles.find(role => role.externalId === employee.roleExternalId)?.name || employee.roleExternalId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={employee.isActive ? t('employees.status.active') : t('employees.status.inactive')}
                        size="small"
                        color={employee.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(employee.createdAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(employee.id)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(employee.id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredEmployees.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        {t('employees.noEmployeesFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>

      {/* Add Employee Modal */}
      <Dialog
        open={isAddModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">{t('employees.modals.add.title')}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('employees.modals.add.firstName')}</InputLabel>
                <OutlinedInput
                  value={newEmployee.firstName}
                  onChange={handleInputChange('firstName')}
                  label={t('employees.modals.add.firstName')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('employees.modals.add.lastName')}</InputLabel>
                <OutlinedInput
                  value={newEmployee.lastName}
                  onChange={handleInputChange('lastName')}
                  label={t('employees.modals.add.lastName')}
                />
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('employees.modals.add.email')}</InputLabel>
                <OutlinedInput
                  type="email"
                  value={newEmployee.email}
                  onChange={handleInputChange('email')}
                  label={t('employees.modals.add.email')}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('employees.modals.add.phone')}</InputLabel>
                <OutlinedInput
                  type="tel"
                  value={newEmployee.phoneNumber}
                  onChange={handleInputChange('phoneNumber')}
                  label={t('employees.modals.add.phone')}
                />
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{t('employees.modals.add.role')}</InputLabel>
                <Select
                  label={t('employees.modals.add.role')}
                  value={newEmployee.roleId}
                  onChange={handleRoleChange}
                >
                  {roles.map(role => (
                    <MenuItem key={role.externalId} value={role.externalId}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>{t('employees.modals.add.password')}</InputLabel>
                <OutlinedInput
                  type="password"
                  value={newEmployee.password}
                  onChange={handleInputChange('password')}
                  label={t('employees.modals.add.password')}
                />
              </FormControl>
            </Box>

            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Switch
                    checked={newEmployee.isActive}
                    onChange={handleSwitchChange}
                    color="primary"
                  />
                }
                label={t('employees.modals.add.active')}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseModal} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSaveEmployee}
            variant="contained"
            color="primary"
          >
            {t('employees.modals.add.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Employee Modal */}
      {editingEmployee && (
        <Dialog
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">{t('employees.modals.edit.title')}</Typography>
              <IconButton onClick={handleCloseEditModal} size="small">
                <Close />
              </IconButton>
          </Box>
        </DialogTitle>

          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('employees.modals.edit.firstName')}</InputLabel>
                  <OutlinedInput
                    value={editingEmployee.firstName}
                    onChange={handleEditInputChange('firstName')}
                    label={t('employees.modals.edit.firstName')}
                    required
                  />
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('employees.modals.edit.lastName')}</InputLabel>
                  <OutlinedInput
                    value={editingEmployee.lastName}
                    onChange={handleEditInputChange('lastName')}
                    label={t('employees.modals.edit.lastName')}
                    required
                  />
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('employees.modals.edit.email')}</InputLabel>
                  <OutlinedInput
                    type="email"
                    value={editingEmployee.email}
                    onChange={handleEditInputChange('email')}
                    label={t('employees.modals.edit.email')}
                    required
                  />
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>{t('employees.modals.edit.phone')}</InputLabel>
                  <OutlinedInput
                    type="tel"
                    value={editingEmployee.phoneNumber}
                    onChange={handleEditInputChange('phoneNumber')}
                    label={t('employees.modals.edit.phone')}
                    required
                  />
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{t('employees.modals.edit.role')}</InputLabel>
                  <Select
                    label={t('employees.modals.edit.role')}
                    value={editingEmployee.roleExternalId}
                    onChange={e =>
                      setEditingEmployee(prev =>
                        prev
                          ? {
                              ...prev,
                            roleExternalId: e.target.value as string,
                            }
                          : null,
                      )
                    }
                  >
                    {roles.map(role => (
                      <MenuItem key={role.externalId} value={role.externalId}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editingEmployee.isActive}
                        onChange={handleEditSwitchChange}
                        color="primary"
                      />
                    }
                    label={t('employees.modals.edit.active')}
                  />
                </FormControl>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseEditModal} color="secondary">
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleUpdateEmployee}
              variant="contained"
              color="primary"
            >
              {t('employees.modals.edit.update')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{t('employees.modals.delete.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('employees.modals.delete.message', {
              name: deletingEmployee
                ? `${deletingEmployee.firstName} ${deletingEmployee.lastName}`
                : ''
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          variant="filled"
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeesPage;
